import { AssignmentInd, Link } from '@mui/icons-material';
import {
  Box,
  Card,
  CardHeader,
  Grid,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import Skeleton from '@mui/material/Skeleton';
import { getSignerInformation } from 'lib/api/integrations/digitalSignature';
import SectionCard from 'lib/common/GeneralComponents/TenderModal/Components/SectionCard';
import { Portfolio, SignerOnPortfolio, User } from 'lib/types/models';
import React, { useState } from 'react';
import { useEffect } from 'react';
import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'services/redux/store';

const Signer: React.FC<{ signer: SignerOnPortfolio }> = ({ signer }) => {
  const dispatch = useDispatch();
  const { user } = useSelector((state: RootState) => state.auth);
  const [loading, setLoading] = useState(false);
  const [information, setInformation] = useState<User | null>(null);
  const showURL = useMemo(() => {
    return (
      signer.url &&
      signer.url !== '' &&
      user?.email === information?.email &&
      information?.email !== null
    );
  }, [signer.url, information?.email, user?.email]);

  const fetchSignerInformation = async () => {
    try {
      setLoading(true);
      if (!signer.providerIdentifier) return;
      const signerInformation = await getSignerInformation(
        dispatch,
        signer.providerIdentifier,
      );
      setInformation(signerInformation);
    } catch (error) {
      console.error('Error fetching signer information:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchSignerInformation();
  }, [signer.providerIdentifier]);

  if (loading) return <Skeleton variant="rectangular" height={90} />;

  return (
    <Grid item xs={6}>
      <Card
        variant="outlined"
        sx={{
          borderRadius: 1.25,
          borderColor: 'divider',
          backgroundColor: 'grey.50',
          height: '100%',
        }}
      >
        <CardHeader
          avatar={<AssignmentInd color="primary" sx={{ fontSize: 18 }} />}
          title={
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography
                variant="caption"
                fontWeight={600}
                color="text.primary"
                sx={{ fontSize: '0.7rem' }}
              >
                {information?.name ?? 'No disponible'}
              </Typography>
              {showURL && (
                <IconButton
                  onClick={() => {
                    if (signer.url) window.open(signer.url, '_blank');
                  }}
                >
                  <Link sx={{ fontSize: 16, color: 'primary.main' }} />
                </IconButton>
              )}
            </Box>
          }
          sx={{ py: 0.5, px: 1 }}
        />
        <Stack spacing={0.25} sx={{ px: 1, pb: 0.75 }}>
          <Typography
            variant="caption"
            color="text.primary"
            sx={{ fontSize: '0.7rem' }}
          >
            {information?.identifier ?? 'No disponible'}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontSize: '0.65rem' }}
          >
            {information?.email ?? 'No disponible'}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontSize: '0.65rem' }}
          >
            {information?.phone ?? 'No disponible'}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontSize: '0.65rem' }}
          >
            Orden: {signer.order}
          </Typography>
        </Stack>
      </Card>
    </Grid>
  );
};

const Signers: React.FC<{ portfolio: Portfolio }> = ({ portfolio }) => {
  return (
    <SectionCard
      title="Firmantes del Portafolio"
      chipLabel="Autorizados para firmar"
      tooltip={
        'Lista de firmantes asignados a este portafolio. Estos son los identificadores que pueden firmar los documentos asociados.'
      }
    >
      {portfolio.signers && portfolio.signers.length > 0 ? (
        <Grid container spacing={1}>
          {portfolio.signers
            .sort((a, b) => (a.order || 0) - (b.order || 0))
            .map((signer: SignerOnPortfolio) => (
              <Signer
                key={`${signer?.providerIdentifier}-${signer?.portfolioId}`}
                signer={signer}
              />
            ))}
        </Grid>
      ) : (
        <Typography variant="body2">
          No hay firmantes asignados a este portafolio.
        </Typography>
      )}
    </SectionCard>
  );
};

export default Signers;
